import { warn } from "@/lib/log";
import { useEffect, useRef, useState } from "react";
import { Send, Cpu } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtTime } from "@/lib/langs";
import { useSupport } from "@/context/SupportContext";

export const SupportInbox = ({ embedded = false }) => {
  const { unread: ctxUnread, markRead } = useSupport();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/support");
      setMsgs(data);
    } catch (e) {
      warn("Support inbox load failed", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { load(); }, [ctxUnread]);
  useEffect(() => {
    if (embedded) markRead();
  }, [embedded, markRead, ctxUnread]);
  const tid = embedded ? "support-sheet-" : "support-";
  const read = () => {
    markRead();
    setMsgs((l) => l.map((m) => ({ ...m, read_by_user: true })));
  };
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/support", { text });
      read();
      setMsgs((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const unread = msgs.filter((m) => m.sender === "admin" && !m.read_by_user).length;

  return (
    <div className={`holo-panel flex flex-col ${embedded ? "h-[calc(100vh-170px)]" : "h-[460px]"}`} data-testid={`${tid}inbox`} onClick={read} onFocus={read}>
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="flex items-center gap-3">
          <Cpu size={16} className="text-[color:var(--au-green)]" />
          <span className="font-mono text-xs font-semibold tracking-widest">SYSTEM / SPEECHFLOW SUPPORT</span>
        </div>
        {unread > 0 && <span className="bg-[color:var(--au-red)] px-2 py-0.5 font-mono text-[10px] text-white" data-testid={`${tid}unread-badge`}>{unread} NEW</span>}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid={`${tid}messages`}>
        {msgs.length === 0 && <p className="font-mono text-xs text-muted-foreground caret">&gt; Channel open. Messages from SpeechFlow Support appear here</p>}
        {msgs.map((m) => (
          <div key={m.message_id} className={`max-w-[85%] ${m.sender === "user" ? "ml-auto text-right" : ""}`} data-testid={`${tid}message`}>
            <div className="mono-label !text-[10px]">{m.sender === "admin" ? "SYSTEM / SpeechFlow Support" : "You"} · {fmtTime(m.created_at)}</div>
            <div className={`mt-1 inline-block px-3 py-2 text-sm ${m.sender === "admin" ? "border-l-2 border-[color:var(--au-green)] bg-cyan-400/10 font-mono text-[13px]" : "bg-muted"}`}>{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-border p-3">
        <input className="field" placeholder="Message SpeechFlow Support…" value={text} onChange={(e) => setText(e.target.value)} data-testid={`${tid}input`} />
        <button className="icon-btn !h-11 !w-11 shrink-0" data-testid={`${tid}send-button`} aria-label="Send"><Send size={16} /></button>
      </form>
    </div>
  );
};
